import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useCurrentUser } from 'hooks/new/useCurrentUser';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import {
  goToAllSpaces,
  goToConversations,
  goToEvents,
  goToHome,
  goToSearch,
} from 'utils/routerHelpers';

import React, { useMemo } from 'react';

import {
  CalendarBlank,
  ChatsCircle,
  House,
  MagnifyingGlass,
  Users,
} from '@thinkific/chalk/react-icons';
import { GlobalNav } from '@thinkific/chalk/recipes/GlobalNav';

import { useCommunityContext } from 'components/CommunityContext';
import { useChatContext } from 'components/new/chat/providers/ChatProvider/ChatProvider';

import * as css from './MobileChin.css';

interface MobileChinProps {
  community: {
    name: string;
    id: string;
  };
}

export const MobileChin = ({ community }: MobileChinProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { trackEvent, inLearnerHub } = useCommunityContext();
  const { isDirectMessagingEnabled, hasUnreadMessages } = useChatContext();
  const { interactions } = useCurrentUser(community.id);

  const hasUnreadSpaces = useMemo(() => {
    return interactions.some((interaction) => interaction.hasUnreadPosts);
  }, [interactions]);
  const menuItems = [
    {
      id: 'home',
      dataUserPilotId: 'home-mobile-tab',
      label: t('pages-home_title', 'Home'),
      icon: House,
      href: `/communities/${community.id}/`,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.GO_TO_HOME, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToHome(router, community.id);
      },
    },
    {
      id: 'spaces',
      dataUserPilotId: 'spaces-mobile-tab',
      label: t('pages-spaces_title', 'Spaces'),
      icon: Users,
      href: `/communities/${community.id}/spaces`,
      notificationDot: hasUnreadSpaces,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.GO_TO_SPACES, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToAllSpaces(router, community.id);
      },
    },
    ...(isDirectMessagingEnabled
      ? [
          {
            id: 'conversations',
            dataUserPilotId: 'conversations-mobile-tab',
            label: t('pages-conversations_title', 'DMs'),
            icon: ChatsCircle,
            href: `/communities/${community.id}/conversations`,
            notificationDot: hasUnreadMessages,
            onClick: (e) => {
              e.preventDefault();
              goToConversations(router, community.id);
            },
          },
        ]
      : []),
    {
      id: 'events',
      dataUserPilotId: 'events-mobile-tab',
      label: t('pages-events_title', 'Events'),
      icon: CalendarBlank,
      href: `/communities/${community.id}/events`,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.CLICK_SEE_ALL_EVENTS, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToEvents(router, community.id);
      },
    },
    {
      id: 'search',
      dataUserPilotId: 'search-mobile-tab',
      label: t('pages-search_title', 'Search'),
      icon: MagnifyingGlass,
      href: `/communities/${community.id}/search`,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.GO_TO_SEARCH, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToSearch(router, community.id);
      },
    },
  ];

  // TODO: Hiding this for now, to revisit when we're further along build
  if (inLearnerHub) return <></>;

  return (
    <div className={css.container} data-qa="mobile-chin">
      <GlobalNav
        currentItemId={menuItems.find((item) => router.route.includes(item.id))?.id || 'home'}
        items={menuItems}
      />
    </div>
  );
};

export default MobileChin;
